import React, { useState, useContext } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ImageView from "@component/ImageView";
import { useTranslation } from "react-i18next";
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useReservationCancelMutation, useReservationAcceptMutation } from "@mutation/reservationMutation";
import { AlertContext } from "@context/AlertProvider";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

const ReservationDetails = ({ reservation, onAccept, onCancel }) => {
  const { t } = useTranslation(["main", "reservations", "error"]);
  const { addAlertBanner } = useContext(AlertContext);
  const [rows, setRows] = useState(reservation.reservation_items.map(item => ({
    ...item,
    selectedQuantity: item.quantity // Initialize with full quantity
  })));
  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openUnavailableItemsDialog, setOpenUnavailableItemsDialog] = useState(false);
  const [unavailableItems, setUnavailableItems] = useState([]);

  const reservationCancelMutation = useReservationCancelMutation();
  const reservationAcceptMutation = useReservationAcceptMutation();

  const isBusyReservationUpdate = reservationAcceptMutation.isPending || reservationCancelMutation.isPending;

  const formatDate = (dateString) => {
    if (!dateString) return t("main:N/A");
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleQuantityChange = (id, newValue) => {
    // Ensure the value is a number between 0 and the max quantity
    const row = rows.find(r => r.uuid === id);
    let quantity = Number(newValue);
    
    if (isNaN(quantity)) {
      quantity = 0;
    } else if (quantity < 0) {
      quantity = 0;
    } else if (quantity > row.quantity) {
      quantity = row.quantity;
    }
    
    setRows(rows.map(row => 
      row.uuid === id ? { ...row, selectedQuantity: quantity } : row
    ));
  };

  const handleIncrement = (id) => {
    const row = rows.find(r => r.uuid === id);
    if (row.selectedQuantity < row.quantity) {
      handleQuantityChange(id, row.selectedQuantity + 1);
    }
  };

  const handleDecrement = (id) => {
    const row = rows.find(r => r.uuid === id);
    if (row.selectedQuantity > 0) {
      handleQuantityChange(id, row.selectedQuantity - 1);
    }
  };

  const handleAcceptReservation = () => {
    // Prepare the data for submission
    const updatedItems = rows.map(row => ({
      site_shop_item_uuid: row.site_shop_item.uuid,
      quantity: row.selectedQuantity
    }));
    
    reservationAcceptMutation.mutate({ data: { reservation_uuid: reservation.uuid, reservation_items: updatedItems}}, {
      onSuccess: (response) => {
        setOpenAcceptDialog(false);
        addAlertBanner(t("reservations:reservationIsAccepted"), "info");
        onAccept?.();
      },
      onError: (error) => {
        if (error.response?.status === 400 && 
            error.response?.data?.detail?.unavailable_items?.length > 0) {
          
          // Map unavailable items with their names from the rows data
          const extendedUnavailableItems = error.response.data.detail.unavailable_items.map(unavailableItem => {
            // Find the corresponding row
            const row = rows.find(r => r.site_shop_item.uuid === unavailableItem.uuid);

            console.log(row);
            
            return {
              ...unavailableItem,
              name: row?.site_shop_item?.shop_item?.name || 'Unknown item'
            };
          });

          setUnavailableItems(extendedUnavailableItems);
          setOpenUnavailableItemsDialog(true);
        } else if (error.response?.status === 400 && 
            error.response?.data?.detail === "No valid items to process in reservation") {
          addAlertBanner(t("error:noValidItemsToProcessInReservation" ), "error", 4000);
        } else if (error.response?.status === 402 && 
            error.response?.data?.detail === "Insufficient points balance") {
          addAlertBanner(t("error:insufficientPointsBalance" ), "error", 4000);
        } else {
          addAlertBanner(t("error:somethingGoesWrong"), "error", 4000);
        }
        setOpenAcceptDialog(false);
      }
    });
  };

  const handleCancelReservation = () => {
    reservationCancelMutation.mutate({ data: { reservation_uuid: reservation.uuid }}, {
      onSuccess: (response) => {
        setOpenCancelDialog(false);
        addAlertBanner(t("reservations:reservationIsCanceled"), "info");
        onCancel?.();
      },
      onError: (error) => {
        addAlertBanner(error.message, "error");
        setOpenCancelDialog(false);
      }
    });
  };

  const columns = [
    { 
      field: 'image', 
      headerName: t("reservations:image"), 
      flex: 1,
      renderCell: (params) => (
        <ImageView
          imageSx={{maxHeight: '100px'}}
          fileUuid={params.row.site_shop_item.shop_item.file_image?.uuid}
        />
      ),
      sortable: false,
      filterable: false
    },
    { 
      field: 'name', 
      headerName: t("reservations:itemName"), 
      flex: 1,
      valueGetter: (params, row) => row.site_shop_item.shop_item.name
    },
    { 
      field: 'quantity', 
      headerName: t("reservations:quantity"), 
      flex: 1,
    },
    { 
      field: 'selection', 
      headerName: t("reservations:selectQuantity"), 
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          height: '100%',
          gap: 1
        }}>
          <IconButton 
            size="small" 
            onClick={() => handleDecrement(params.row.uuid)}
            disabled={params.row.selectedQuantity <= 0}
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          
          <TextField
            value={params.row.selectedQuantity}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow numeric input
              if (/^\d*$/.test(value)) {
                handleQuantityChange(params.row.uuid, value === '' ? 0 : parseInt(value, 10));
              }
            }}
            inputProps={{
              style: { 
                textAlign: 'center',
                width: '60px'
              }
            }}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'divider'
                },
                '&:hover fieldset': {
                  borderColor: 'text.primary'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '1px'
                }
              }
            }}
          />
          
          <IconButton 
            size="small" 
            onClick={() => handleIncrement(params.row.uuid)}
            disabled={params.row.selectedQuantity >= params.row.quantity}
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
      sortable: false,
      filterable: false
    },
  ];

  const isPendingStatus = () => {
    const isExpiredReservation = reservation.reservation_type?.name === "expired";
    return !isExpiredReservation && reservation.canceled_at == null && reservation.approved_at == null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column'}}>
      {/* Information Table Section */}
      <Box sx={{ 
        p: 2, 
        bgcolor: 'background.paper', 
        borderRadius: 1,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 2
      }}>
        {/* Row 1 - Cell 1 (Customer) */}
        <Box>
          <Typography variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.66,
              display: 'block',
              mb: 0.5
            }}>
            {t("reservations:customer")}:
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonIcon fontSize="small" />
            <Typography variant="body1">
              {reservation.account.first_name} {reservation.account.last_name}
            </Typography>
          </Stack>
        </Box>

        {/* Row 1 - Cell 2 (Reserved At) */}
        <Box>
          <Typography variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.66,
              display: 'block',
              mb: 0.5
            }}>
            {t("reservations:reservedAt")}:
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarMonthIcon fontSize="small" />
            <Typography variant="body1">{formatDate(reservation.reserved_at)}</Typography>
          </Stack>
        </Box>

        {/* Row 2 - Cell 1 (Expired At) */}
        <Box>
          <Typography variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.66,
              display: 'block',
              mb: 0.5
            }}>
            {t("reservations:expiredAt")}:
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarMonthIcon fontSize="small" />
            <Typography variant="body1">{formatDate(reservation.expired_at)}</Typography>
          </Stack>
        </Box>

        {/* Row 2 - Cell 2 (Approved At) */}
        <Box>
          <Typography variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.75rem',
              lineHeight: 1.66,
              display: 'block',
              mb: 0.5
            }}>
            {t("reservations:approvedAt")}:
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarMonthIcon fontSize="small" />
            <Typography variant="body1">{formatDate(reservation.approved_at)}</Typography>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ p: 2, width:'100%' }}>
        <DataGrid
          rowHeight={100}
          rows={rows}
          columns={columns}
          autosizeOnMount={true}
          rowsPerPageOptions={[]}
          getRowId={(row) => row.uuid}
          disableSelectionOnClick
          hideFooter={true}
          disableColumnMenu={true}
        />
      </Box>

      {/* Action Buttons */}
      {
        isPendingStatus() ? 
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="error"
            onClick={() => setOpenCancelDialog(true)}
          >
            {t("main:cancel")}
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setOpenAcceptDialog(true)}
          >
            {t("main:confirm")}
          </Button>
        </Box> : null
      }
      

      {/* Accept Reservation Dialog */}
      <Dialog
        open={openAcceptDialog}
        onClose={() => setOpenAcceptDialog(false)}
      >
        <DialogTitle>{t("reservations:confirmAcceptTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("reservations:confirmAcceptMessage")}
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            {rows.map(row => (
              <Typography key={row.uuid}>
                {row.site_shop_item.shop_item.name}: {row.selectedQuantity} / {row.quantity}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAcceptDialog(false)} >
            {t("main:cancel")}
          </Button>
          <Button onClick={handleAcceptReservation} color="primary" autoFocus  variant="contained"  disabled={isBusyReservationUpdate}>
            {t("main:confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Reservation Dialog */}
      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
      >
        <DialogTitle>{t("reservations:confirmCancelTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("reservations:confirmCancelMessage")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>
            {t("main:cancel")}
          </Button>
          <Button onClick={handleCancelReservation} color="error" autoFocus>
            {t("main:confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unavailable Items Dialog */}
      <Dialog
        open={openUnavailableItemsDialog}
        onClose={() => setOpenUnavailableItemsDialog(false)}
      >
        <DialogTitle>{t("reservations:unavailableItemsTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("reservations:unavailableItemsMessage")}
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            {unavailableItems.map(item => (
              <Typography key={item.uuid}>
                {item.name}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUnavailableItemsDialog(false)}>
            {t("main:confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReservationDetails;
